const DEFAULT_PATH = '/';
const LOGIN_PATH = '/login';

/**
 * Valida y normaliza una URL de retorno para prevenir ataques de open redirect.
 *
 * Esta función se usa cuando el usuario es redirigido al login desde una ruta
 * protegida (ej. un link desde un correo), y tras autenticarse debe volver a
 * su destino original. Como el destino llega vía query param (`?returnUrl=...`),
 * un atacante podría manipularlo para apuntar a un sitio externo malicioso.
 *
 * ## Reglas de validación
 *
 * Una URL se considera segura únicamente si es una **ruta interna relativa**.
 * Se rechaza (devolviendo la ruta por defecto `/`) cualquier URL que:
 *
 * - Sea `null`, `undefined` o string vacío.
 * - No comience con `/` (ej. `https://phishing.com`, `javascript:alert(1)`,
 *   `mailto:...`, rutas relativas tipo `../admin`).
 * - Comience con `//` — los navegadores interpretan `//evil.com/path` como
 *   una URL protocolo-relativa que sale del dominio actual.
 * - Apunte al propio `/login` (ej. `/login`, `/login?foo=bar`, `/login/sub`),
 *   ya que generaría un loop de redirección tras autenticarse.
 *
 * ## Referencias
 *
 * - OWASP: Unvalidated Redirects and Forwards
 *   (https://owasp.org/www-community/attacks/Unvalidated_Redirects_and_Forwards)
 *
 * @param url - URL candidata, típicamente leída de `queryParamMap.get('returnUrl')`.
 * @returns La misma URL si pasa todas las validaciones, o `/` si es inválida o insegura.
 *
 * @example
 * // Casos válidos — devuelve la URL tal cual
 * sanitizeReturnUrl('/dashboard');                    // → '/dashboard'
 * sanitizeReturnUrl('/irm/incidents/123');            // → '/irm/incidents/123'
 * sanitizeReturnUrl('/orders/42?tab=details#top');    // → '/orders/42?tab=details#top'
 *
 * @example
 * // Casos inválidos — devuelve '/' por seguridad
 * sanitizeReturnUrl(null);                            // → '/'
 * sanitizeReturnUrl('');                              // → '/'
 * sanitizeReturnUrl('https://phishing.com');          // → '/'  (URL absoluta externa)
 * sanitizeReturnUrl('//evil.com/steal');              // → '/'  (protocolo-relativa)
 * sanitizeReturnUrl('javascript:alert(1)');           // → '/'  (esquema peligroso)
 * sanitizeReturnUrl('../admin');                      // → '/'  (ruta relativa)
 * sanitizeReturnUrl('/login');                        // → '/'  (loop de redirección)
 * sanitizeReturnUrl('/login?returnUrl=/x');           // → '/'  (loop anidado)
 *
 * @example
 * // Uso típico en un componente de login
 * const raw = this.route.snapshot.queryParamMap.get('returnUrl');
 * const target = sanitizeReturnUrl(raw);
 * this.router.navigateByUrl(target);
 */
export function sanitizeReturnUrl(url: string | null | undefined): string {
    if (!url) return DEFAULT_PATH;
    if (!url.startsWith('/') || url.startsWith('//')) return DEFAULT_PATH;
    if (
        url === LOGIN_PATH ||
        url.startsWith(`${LOGIN_PATH}?`) ||
        url.startsWith(`${LOGIN_PATH}/`)
    ) {
        return DEFAULT_PATH;
    }
    return url;
}